import { useEffect, useRef, useState, useMemo, useCallback } from 'react'

/**
 * Interface for individual particle configuration
 * Defines the properties and behavior of each animated particle
 */
interface Particle {
  id: number
  x: number
  y: number
  size: number
  speedX: number
  speedY: number
  opacity: number
  color: string
  type: 'dot' | 'ring' | 'square'
  life: number
  maxLife: number
}

/**
 * Interface for floating orb configuration
 * Defines larger decorative elements that float around the screen
 */
interface FloatingOrb {
  id: number
  x: number
  y: number
  size: number
  color: string
  animationDelay: number
  duration: number
}

/**
 * Props for the BackgroundEffects component
 * Allows customization of the visual effects
 */
interface BackgroundEffectsProps {
  /** Number of particles to render */
  particleCount?: number
  /** Number of floating orbs */
  orbCount?: number
  /** Whether to show grid overlay */
  showGrid?: boolean
  /** Whether to show aurora effects */
  showAurora?: boolean
  /** Animation speed multiplier */
  animationSpeed?: number
  /** Whether the effects should be visible */
  enabled?: boolean
}

/**
 * BackgroundEffects Component - Advanced particle system and visual effects
 * 
 * This component creates a sophisticated background with:
 * - Animated particles with various shapes and behaviors
 * - Floating orbs with glassmorphism effects
 * - Dynamic grid overlay with cyberpunk styling
 * - Aurora/lighting effects
 * - Performance optimizations and accessibility support
 */
export function BackgroundEffects({
  particleCount = 50,
  orbCount = 6,
  showGrid = true,
  showAurora = true,
  animationSpeed = 1,
  enabled = true
}: BackgroundEffectsProps): JSX.Element {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const animationFrameRef = useRef<number>()
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 })
  const [isVisible, setIsVisible] = useState(true)
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false)

  // Check for reduced motion preference
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)')
    setPrefersReducedMotion(mediaQuery.matches)
    
    const handleChange = (e: MediaQueryListEvent) => {
      setPrefersReducedMotion(e.matches)
    }
    
    mediaQuery.addEventListener('change', handleChange)
    return () => mediaQuery.removeEventListener('change', handleChange)
  }, [])

  /**
   * Handle window resize to update canvas dimensions
   * Ensures the background effects cover the full viewport
   */
  const handleResize = useCallback(() => {
    setDimensions({
      width: window.innerWidth,
      height: window.innerHeight
    })
  }, [])

  // Set up resize listener and initial dimensions
  useEffect(() => {
    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [handleResize])

  /**
   * Create initial particles with random properties
   * Generates particles with varied characteristics for visual interest
   */
  const createParticles = useCallback((count: number): Particle[] => {
    const particles: Particle[] = []
    const colors = ['#00E0FF', '#FF00CC', '#9945FF', '#00FF88', '#FFED4E']
    const types: Particle['type'][] = ['dot', 'ring', 'square']
    
    for (let i = 0; i < count; i++) {
      const colorIndex = Math.floor(Math.random() * colors.length)
      const typeIndex = Math.floor(Math.random() * types.length)
      
      particles.push({
        id: i,
        x: Math.random() * dimensions.width,
        y: Math.random() * dimensions.height,
        size: Math.random() * 3 + 1,
        speedX: (Math.random() - 0.5) * 0.5 * animationSpeed,
        speedY: (Math.random() - 0.5) * 0.5 * animationSpeed,
        opacity: Math.random() * 0.6 + 0.2,
        color: colors[colorIndex] || '#00E0FF',
        type: types[typeIndex] || 'dot',
        life: Math.random() * 200 + 100,
        maxLife: 300
      })
    }
    return particles
  }, [dimensions.width, dimensions.height, animationSpeed])

  /**
   * Create floating orbs with glassmorphism effects
   * Generates larger decorative elements that add depth to the background
   */
  const floatingOrbs = useMemo((): FloatingOrb[] => {
    const orbs: FloatingOrb[] = []
    const colors = [
      'rgba(0, 224, 255, 0.1)',
      'rgba(255, 0, 204, 0.1)',
      'rgba(153, 69, 255, 0.1)',
      'rgba(0, 255, 136, 0.1)'
    ]
    
    for (let i = 0; i < orbCount; i++) {
      const colorIndex = Math.floor(Math.random() * colors.length)
      
      orbs.push({
        id: i,
        x: Math.random() * 80 + 10, // Percentage based positioning
        y: Math.random() * 80 + 10,
        size: Math.random() * 300 + 200,
        color: colors[colorIndex] || 'rgba(0, 224, 255, 0.1)',
        animationDelay: Math.random() * 10,
        duration: Math.random() * 20 + 15
      })
    }
    return orbs
  }, [orbCount])

  /**
   * Update particle positions and properties
   * Handles particle movement, lifecycle, and boundary conditions
   */
  const updateParticles = useCallback((particles: Particle[]): Particle[] => {
    return particles.map(particle => {
      // Update position
      let newX = particle.x + particle.speedX
      let newY = particle.y + particle.speedY
      
      // Bounce off walls
      if (newX <= 0 || newX >= dimensions.width) {
        particle.speedX *= -1
        newX = Math.max(0, Math.min(dimensions.width, newX))
      }
      if (newY <= 0 || newY >= dimensions.height) {
        particle.speedY *= -1
        newY = Math.max(0, Math.min(dimensions.height, newY))
      }
      
      // Update life
      const newLife = particle.life - 1
      const lifeRatio = newLife / particle.maxLife
      
      return {
        ...particle,
        x: newX,
        y: newY,
        life: newLife,
        opacity: Math.max(0.1, lifeRatio * 0.8)
      }
    }).filter(particle => particle.life > 0)
  }, [dimensions.width, dimensions.height])

  /**
   * Render a single particle on the canvas
   * Draws different shapes based on particle type
   */
  const drawParticle = useCallback((
    ctx: CanvasRenderingContext2D,
    particle: Particle
  ) => {
    ctx.save()
    ctx.globalAlpha = particle.opacity
    ctx.fillStyle = particle.color
    ctx.strokeStyle = particle.color
    ctx.lineWidth = 1
    
    const x = particle.x
    const y = particle.y
    const size = particle.size
    
    switch (particle.type) {
      case 'dot':
        ctx.beginPath()
        ctx.arc(x, y, size, 0, Math.PI * 2)
        ctx.fill()
        
        // Add glow effect
        ctx.shadowColor = particle.color
        ctx.shadowBlur = size * 2
        ctx.fill()
        break
        
      case 'ring':
        ctx.beginPath()
        ctx.arc(x, y, size, 0, Math.PI * 2)
        ctx.stroke()
        
        // Inner ring
        ctx.beginPath()
        ctx.arc(x, y, size * 0.5, 0, Math.PI * 2)
        ctx.stroke()
        break
        
      case 'square':
        ctx.fillRect(x - size, y - size, size * 2, size * 2)
        
        // Add glow effect
        ctx.shadowColor = particle.color
        ctx.shadowBlur = size * 2
        ctx.fillRect(x - size, y - size, size * 2, size * 2)
        break
    }
    
    ctx.restore()
  }, [])

  /**
   * Main animation loop
   * Handles particle updates and canvas rendering
   */
  const animate = useCallback(() => {
    const canvas = canvasRef.current
    if (!canvas || !enabled || !isVisible || prefersReducedMotion) return

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    // Clear canvas
    ctx.clearRect(0, 0, dimensions.width, dimensions.height)

    // Update and render particles
    let currentParticles = createParticles(particleCount)
    
    const renderFrame = () => {
      if (!canvas || !enabled || !isVisible) return
      
      ctx.clearRect(0, 0, dimensions.width, dimensions.height)
      
      // Update particles
      currentParticles = updateParticles(currentParticles)
      
      // Add new particles if needed
      while (currentParticles.length < particleCount) {
        const newParticles = createParticles(1)
        if (newParticles.length > 0 && newParticles[0]) {
          currentParticles.push(newParticles[0])
        }
      }
      
      // Draw particles
      currentParticles.forEach(particle => {
        drawParticle(ctx, particle)
      })
      
      // Continue animation if conditions are met
      if (enabled && isVisible && !prefersReducedMotion) {
        animationFrameRef.current = requestAnimationFrame(renderFrame)
      }
    }
    
    renderFrame()
  }, [
    enabled,
    isVisible,
    prefersReducedMotion,
    dimensions.width,
    dimensions.height,
    particleCount,
    createParticles,
    updateParticles,
    drawParticle
  ])

  // Start animation when component mounts or dependencies change
  useEffect(() => {
    if (dimensions.width > 0 && dimensions.height > 0) {
      animate()
    }
    
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current)
      }
    }
  }, [animate, dimensions])

  // Handle visibility changes for performance
  useEffect(() => {
    const handleVisibilityChange = () => {
      setIsVisible(!document.hidden)
    }
    
    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange)
  }, [])

  if (!enabled || prefersReducedMotion) {
    return <div className="fixed inset-0 pointer-events-none z-0" />
  }

  return (
    <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden">
      {/* Particle Canvas */}
      <canvas
        ref={canvasRef}
        width={dimensions.width}
        height={dimensions.height}
        className="absolute inset-0 opacity-60"
        style={{
          mixBlendMode: 'screen'
        }}
      />
      
      {/* Floating Orbs */}
      {floatingOrbs.map((orb) => (
        <div
          key={orb.id}
          className="absolute rounded-full blur-3xl animate-float opacity-30"
          style={{
            left: `${orb.x}%`,
            top: `${orb.y}%`,
            width: `${orb.size}px`,
            height: `${orb.size}px`,
            backgroundColor: orb.color,
            animationDelay: `${orb.animationDelay}s`,
            animationDuration: `${orb.duration}s`,
            transform: 'translate(-50%, -50%)'
          }}
        />
      ))}
      
      {/* Grid Overlay */}
      {showGrid && (
        <div className="absolute inset-0 opacity-10">
          <svg
            width="100%"
            height="100%"
            className="w-full h-full"
          >
            <defs>
              <pattern
                id="cyber-grid"
                width="50"
                height="50"
                patternUnits="userSpaceOnUse"
              >
                <path
                  d="M 50 0 L 0 0 0 50"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="0.5"
                  className="text-cyber-cyan-500"
                />
              </pattern>
              <linearGradient id="grid-fade" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" style={{ stopColor: '#00E0FF', stopOpacity: 0.1 }} />
                <stop offset="50%" style={{ stopColor: '#FF00CC', stopOpacity: 0.05 }} />
                <stop offset="100%" style={{ stopColor: '#9945FF', stopOpacity: 0.1 }} />
              </linearGradient>
            </defs>
            <rect width="100%" height="100%" fill="url(#cyber-grid)" />
            <rect width="100%" height="100%" fill="url(#grid-fade)" />
          </svg>
        </div>
      )}
      
      {/* Aurora Effects */}
      {showAurora && (
        <div className="absolute inset-0 opacity-20">
          <div
            className="absolute inset-0"
            style={{
              background: `
                radial-gradient(ellipse 800px 600px at 20% 40%, rgba(0, 224, 255, 0.15) 0%, transparent 50%),
                radial-gradient(ellipse 600px 800px at 80% 70%, rgba(255, 0, 204, 0.15) 0%, transparent 50%),
                radial-gradient(ellipse 400px 600px at 60% 20%, rgba(153, 69, 255, 0.15) 0%, transparent 50%)
              `,
              animation: 'aurora 20s ease-in-out infinite',
              mixBlendMode: 'screen'
            }}
          />
        </div>
      )}
      
      {/* Scanline Effect */}
      <div className="absolute inset-0 opacity-5 pointer-events-none">
        <div
          className="w-full h-px bg-cyber-cyan-500 animate-pulse absolute"
          style={{
            top: '30%',
            boxShadow: '0 0 10px #00E0FF',
            animation: 'wave 4s ease-in-out infinite'
          }}
        />
        <div
          className="w-full h-px bg-cyber-magenta-500 animate-pulse absolute"
          style={{
            top: '70%',
            boxShadow: '0 0 10px #FF00CC',
            animation: 'wave 4s ease-in-out infinite 2s'
          }}
        />
      </div>
      
      {/* Corner Accents */}
      <div className="absolute top-0 left-0 w-32 h-32 opacity-30">
        <div className="w-full h-px bg-gradient-to-r from-cyber-cyan-500 to-transparent" />
        <div className="w-px h-full bg-gradient-to-b from-cyber-cyan-500 to-transparent" />
      </div>
      <div className="absolute top-0 right-0 w-32 h-32 opacity-30">
        <div className="w-full h-px bg-gradient-to-l from-cyber-magenta-500 to-transparent" />
        <div className="w-px h-full bg-gradient-to-b from-cyber-magenta-500 to-transparent ml-auto" />
      </div>
      <div className="absolute bottom-0 left-0 w-32 h-32 opacity-30">
        <div className="w-px h-full bg-gradient-to-t from-cyber-purple-400 to-transparent" />
        <div className="w-full h-px bg-gradient-to-r from-cyber-purple-400 to-transparent mt-auto" />
      </div>
      <div className="absolute bottom-0 right-0 w-32 h-32 opacity-30">
        <div className="w-px h-full bg-gradient-to-t from-cyber-green-500 to-transparent ml-auto" />
        <div className="w-full h-px bg-gradient-to-l from-cyber-green-500 to-transparent mt-auto" />
      </div>
    </div>
  )
}

/**
 * Default export for easy importing
 */
export default BackgroundEffects